import type { ReactNode } from 'react';

import { Spinner } from './Spinner.js';

interface DataStateProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  onRetry?: () => void;
  children: ReactNode;
  /** Number of columns to span when rendered inside a table body. */
  colSpan?: number;
}

function messageOf(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Something went wrong while loading data.';
}

/**
 * Renders loading / error / empty placeholders, otherwise `children`.
 * When `colSpan` is provided, placeholders render as a single full-width table row.
 */
export function DataState({
  isLoading,
  isError,
  error,
  isEmpty,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  onRetry,
  children,
  colSpan,
}: DataStateProps): JSX.Element {
  let content: ReactNode = null;

  if (isLoading) {
    content = (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500">
        <Spinner className="h-5 w-5" /> Loading…
      </div>
    );
  } else if (isError) {
    content = (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <p className="text-sm font-medium text-rose-600">{messageOf(error)}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Try again
          </button>
        ) : null}
      </div>
    );
  } else if (isEmpty) {
    content = (
      <div className="flex flex-col items-center gap-2 py-12 text-center">
        <p className="text-sm font-medium text-slate-700">{emptyTitle}</p>
        {emptyDescription ? (
          <p className="max-w-sm text-sm text-slate-500">{emptyDescription}</p>
        ) : null}
        {emptyAction ? <div className="mt-2">{emptyAction}</div> : null}
      </div>
    );
  } else {
    return <>{children}</>;
  }

  if (colSpan !== undefined) {
    return (
      <tr>
        <td colSpan={colSpan}>{content}</td>
      </tr>
    );
  }

  return <>{content}</>;
}
